import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { config } from "../config";
import { useConsent } from "../contexts/ConsentContext";

type Gtag = (...args: unknown[]) => void;
type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    fbq?: Fbq;
    _fbq?: Fbq;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

function addScript(id: string, src: string): void {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function deleteFirstPartyCookie(name: string): void {
  const domains = [undefined, location.hostname, ".dwik.art"];
  for (const domain of domains) {
    document.cookie = `${name}=; Max-Age=0; path=/;${
      domain ? ` domain=${domain};` : ""
    } SameSite=Lax`;
  }
}

function enableGoogleAnalytics(): boolean {
  const id = config.googleAnalyticsId;
  window[`ga-disable-${id}`] = false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer?.push(arguments);
  };

  if (!document.getElementById("dwik-google-analytics")) {
    window.gtag("js", new Date());
    window.gtag("config", id, { send_page_view: true, anonymize_ip: true });
    addScript(
      "dwik-google-analytics",
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`,
    );
    return true;
  }

  return false;
}

function disableGoogleAnalytics(): void {
  window[`ga-disable-${config.googleAnalyticsId}`] = true;
  deleteFirstPartyCookie("_ga");
  for (const part of document.cookie.split(";")) {
    const name = part.trim().split("=")[0];
    if (name.startsWith("_ga_")) deleteFirstPartyCookie(name);
  }
}

function enableMetaPixel(): void {
  if (!window.fbq) {
    const fbq = ((...args: unknown[]) => {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    }) as Fbq;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    window.fbq = fbq;
    window._fbq = fbq;
    addScript(
      "dwik-meta-pixel",
      "https://connect.facebook.net/en_US/fbevents.js",
    );
    fbq("init", config.metaPixelId);
  }
  window.fbq("consent", "grant");
}

function disableMetaPixel(): void {
  window.fbq?.("consent", "revoke");
  deleteFirstPartyCookie("_fbp");
  deleteFirstPartyCookie("_fbc");
}

export function TrackingManager() {
  const { consent } = useConsent();
  const location = useLocation();
  const lastGaPath = useRef("");
  const lastMetaPath = useRef("");
  const pagePath = `${window.location.pathname}${window.location.hash}`;

  useEffect(() => {
    if (consent?.analytics) {
      const initialPageViewSent = enableGoogleAnalytics();
      if (initialPageViewSent) lastGaPath.current = pagePath;
    } else {
      disableGoogleAnalytics();
    }
  }, [consent?.analytics]);

  useEffect(() => {
    if (!consent?.analytics || lastGaPath.current === pagePath) return;
    lastGaPath.current = pagePath;
    window.gtag?.("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: pagePath,
      send_to: config.googleAnalyticsId,
    });
  }, [consent?.analytics, location.key, pagePath]);

  useEffect(() => {
    if (consent?.marketing) enableMetaPixel();
    else disableMetaPixel();
  }, [consent?.marketing]);

  useEffect(() => {
    if (!consent?.marketing || lastMetaPath.current === pagePath) return;
    lastMetaPath.current = pagePath;
    window.fbq?.("track", "PageView");
  }, [consent?.marketing, location.key, pagePath]);

  return null;
}
